// `InProgressAction` — обёртка для dimmed-кликабельных элементов handoff'а.
//
// Концепция переноса (roadmap §3): «то, что ещё не реализовано backend/transport-ом,
// всё равно показываем в интерфейсе, но в состоянии disabled/dimmed/in progress».
// Элемент визуально затемнён, остаётся кликабельным, клик открывает toast/modal
// с текстом про статус разработки; никаких фиктивных мутаций.
//
// Решение Шага 12 (roadmap ai-agents-new-frontend): `InProgressAction` остаётся
// **локальным** компонентом (`transport/frontend/src/shared/`). В пакет `@frm/ui`
// его не выносим — это инфраструктура конкретного приложения, а не переиспользуемый
// shared-примитив (`market`/`metrics`/`launcher` уже live, без dimmed-зон).
//
// Использование (типовое — кнопка-action в карточке):
//   <InProgressAction id="agents.release" render={(props) => (
//     <Button {...props} variant="ghost" size="sm">Выпустить</Button>
//   )} />
// — `props` содержат `onClick` + `aria-disabled='true'` + `data-in-progress-id`,
// сам элемент рендерит потребитель (любая кнопка/иконка/строка таблицы).

import type { CSSProperties, KeyboardEvent, MouseEvent, ReactElement } from 'react'
import { useCallback } from 'react'
import { useToast } from '@frm/ui'

import { getInProgressEntry } from './registry'

export interface InProgressRenderProps {
  /** Обработчик клика — обёртка над toast'ом + preventDefault. */
  onClick: (event: MouseEvent<HTMLElement>) => void
  /** Аналогично onClick для клавиатуры (Enter/Space). */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
  /** Визуальный затемнённый стиль. Потребитель может смерджить со своим. */
  style: CSSProperties
  /** ARIA: элемент disabled-by-meaning, но интерактивный. */
  'aria-disabled': true
  /** Стабильный data-атрибут для тестов и аналитики. */
  'data-in-progress-id': string
  /** Tooltip-текст по умолчанию (короткое объяснение). */
  title: string
  /** Курсор-pointer, чтобы пользователь видел кликабельность. */
  role: 'button'
  tabIndex: 0
}

export interface InProgressActionProps {
  /** Ключ записи в `IN_PROGRESS_REGISTRY`. */
  id: string
  /** Render-prop — потребитель решает, какой DOM-узел рендерить. */
  render: (props: InProgressRenderProps) => ReactElement
  /** Опциональный override стиля dimmed-затемнения. */
  dimmedStyle?: CSSProperties
}

const DEFAULT_DIMMED_STYLE: CSSProperties = {
  // Aurora dimmed-стиль: 55% opacity + не убираем pointer-events
  // (концепция: «остаётся кликабельным»).
  opacity: 0.55,
  cursor: 'pointer',
  // Чуть менее насыщенный фильтр, чтобы dimmed-элемент не сливался с фоном.
  filter: 'saturate(0.85)',
}

export function InProgressAction({ id, render, dimmedStyle }: InProgressActionProps) {
  const { toast } = useToast()

  const showToast = useCallback(() => {
    const entry = getInProgressEntry(id)
    if (entry) {
      toast({
        title: entry.title,
        description: `${entry.description} → ${entry.roadmapStep}`,
        tone: 'info',
        duration: 6000,
      })
      return
    }
    // Fallback на случай, если потребитель забыл зарегистрировать id в registry.ts.
    // Не падаем, но громко сообщаем пользователю и в консоль (для dev).
    toast({
      title: 'В разработке',
      description: `Action id "${id}" не зарегистрирован в IN_PROGRESS_REGISTRY. Появится в следующих шагах спринта ai-agents-new-frontend.`,
      tone: 'warning',
      duration: 6000,
    })
    if (typeof console !== 'undefined') {
      console.warn(`[InProgressAction] unregistered id: ${id}`)
    }
  }, [id, toast])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      showToast()
    },
    [showToast],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        showToast()
      }
    },
    [showToast],
  )

  const entry = getInProgressEntry(id)
  const renderProps: InProgressRenderProps = {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    style: { ...DEFAULT_DIMMED_STYLE, ...(dimmedStyle ?? {}) },
    'aria-disabled': true,
    'data-in-progress-id': id,
    title: entry ? `${entry.title} — в разработке (${entry.roadmapStep})` : 'В разработке',
    role: 'button',
    tabIndex: 0,
  }

  return render(renderProps)
}
